// ShizaiApprovalsAuditCard — live audit trail for the approvals system (iter_143d).
//
// Renders a chronological feed of every approval event (escalated / stalled /
// delegated / approved / rejected / created), auto-refreshing every 60 s so
// admins spot silent auto-escalations within minutes. Filters by event type
// and approver. Backed by GET /api/shizai/approvals/audit-trail.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ClockCounterClockwise, ArrowsClockwise, ArrowRight, Warning, CheckCircle, XCircle,
  ArrowFatUp, PaperPlaneTilt, PlusCircle, HandArrowUp, PauseCircle, BellRinging,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { formatDateTime } from '@/utils/datetime';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;
const REFRESH_MS = 60_000;

// Small tone + icon dictionary for each history event kind. Kept plain instead
// of using a switch so it's easy to extend (e.g. when we add "rerouted").
const EVENT_META = {
  created:    { icon: PlusCircle,   label: '作成',       tone: 'bg-sky-50 text-sky-700 border-sky-200' },
  escalated:  { icon: ArrowFatUp,   label: 'エスカレート', tone: 'bg-amber-50 text-amber-800 border-amber-200' },
  stalled:    { icon: PauseCircle,  label: '停滞',       tone: 'bg-rose-50 text-rose-700 border-rose-200' },
  delegated:  { icon: PaperPlaneTilt, label: '委任',     tone: 'bg-violet-50 text-violet-700 border-violet-200' },
  approved:   { icon: CheckCircle,  label: '承認',       tone: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  rejected:   { icon: XCircle,      label: '却下',       tone: 'bg-zinc-100 text-zinc-700 border-zinc-300' },
};

const FALLBACK_META = { icon: Warning, label: '?', tone: 'bg-zinc-100 text-zinc-700 border-zinc-200' };

export default function ShizaiApprovalsAuditCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [eventFilter, setEventFilter] = useState('all');
  const [approverFilter, setApproverFilter] = useState('');
  const [remindingId, setRemindingId] = useState(null);
  const timerRef = useRef(null);
  const lastFetchedAt = useRef(0);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setLoading(true);
    try {
      const params = { limit: 100 };
      if (eventFilter && eventFilter !== 'all') params.event = eventFilter;
      if (approverFilter.trim()) params.approver = approverFilter.trim();
      const r = await axios.get(`${API}/shizai/approvals/audit-trail`, {
        params, withCredentials: true,
      });
      setData(r.data);
      lastFetchedAt.current = Date.now();
    } catch (err) {
      if (!silent) toast.error(err?.response?.data?.detail || '監査ログの取得に失敗しました');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [eventFilter, approverFilter]);

  // Initial load + on filter change
  useEffect(() => { load(); }, [load]);

  // Silent auto-refresh every 60 s
  useEffect(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => load({ silent: true }), REFRESH_MS);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [load]);

  // iter_143e · Manual reminder handler — hits POST /approvals/{id}/remind.
  // Backend enforces a 4h cooldown so we surface that as a specific toast.
  const remind = async (approvalId, orderNo) => {
    setRemindingId(approvalId);
    try {
      await axios.post(`${API}/shizai/approvals/${approvalId}/remind`, {}, { withCredentials: true });
      toast.success(`リマインド送信: ${orderNo || approvalId}`);
      load({ silent: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || 'リマインド送信に失敗しました';
      if (status === 429) toast.warning(detail);
      else toast.error(detail);
    } finally {
      setRemindingId(null);
    }
  };

  const events = data?.events || [];
  const tickMinutes = data?.tick_seconds ? Math.round(data.tick_seconds / 60) : 15;

  const summary = useMemo(() => {
    const counts = data?.counts || {};
    return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
      <StatChip
        label="24h エスカレート"
        value={counts.escalations_24h ?? 0}
        tone={counts.escalations_24h > 0 ? 'bg-amber-50 border-amber-300 text-amber-800' : 'bg-zinc-50 border-zinc-200 text-zinc-500'}
        testid="audit-stat-esc-24h"
      />
      <StatChip
        label="停滞中（保留）"
        value={counts.stalled_pending ?? 0}
        tone={counts.stalled_pending > 0 ? 'bg-rose-50 border-rose-300 text-rose-800' : 'bg-zinc-50 border-zinc-200 text-zinc-500'}
        testid="audit-stat-stalled"
      />
      <StatChip
        label="累計エスカレート"
        value={counts.by_event?.escalated ?? 0}
        tone="bg-white border-zinc-200 text-zinc-700"
        testid="audit-stat-esc-total"
      />
      <StatChip
        label="表示イベント"
        value={`${counts.shown ?? 0} / ${counts.total_events ?? 0}`}
        tone="bg-white border-zinc-200 text-zinc-700"
        testid="audit-stat-shown"
      />
    </div>
    );
  }, [data]);

  return (
    <div className="rounded-xl border border-zinc-200 bg-white p-4" data-testid="approvals-audit-card">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <div className="text-sm font-bold text-zinc-800 flex items-center gap-1.5">
            <ClockCounterClockwise weight="duotone" className="text-indigo-500" size={18} />
            承認 監査ログ（ライブ）
          </div>
          <p className="text-[11px] text-zinc-500 mt-0.5">
            自動エスカレートや停滞をほぼリアルタイムで確認。
            サーバー側スキャンは {tickMinutes} 分ごと・画面は 60 秒ごとに再取得。
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => load()}
          disabled={loading}
          data-testid="audit-refresh-btn"
        >
          <ArrowsClockwise size={12} className={`mr-1 ${loading ? 'animate-spin' : ''}`} />
          再取得
        </Button>
      </div>

      {summary}

      <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2 items-end">
        <div>
          <Label className="text-[10px] text-zinc-500">イベント種別</Label>
          <Select value={eventFilter} onValueChange={setEventFilter}>
            <SelectTrigger className="h-8 mt-0.5" data-testid="audit-filter-event">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">すべて</SelectItem>
              <SelectItem value="escalated">エスカレートのみ</SelectItem>
              <SelectItem value="stalled">停滞のみ</SelectItem>
              <SelectItem value="delegated">委任</SelectItem>
              <SelectItem value="approved">承認</SelectItem>
              <SelectItem value="rejected">却下</SelectItem>
              <SelectItem value="created">依頼作成</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px] text-zinc-500">承認者（メール部分一致）</Label>
          <Input
            value={approverFilter}
            onChange={(e) => setApproverFilter(e.target.value)}
            placeholder="例: alice@x.com"
            className="h-8 mt-0.5 text-sm"
            data-testid="audit-filter-approver"
          />
        </div>
      </div>

      <div className="mt-3 border border-zinc-200 rounded-lg overflow-hidden divide-y divide-zinc-200 max-h-[520px] overflow-y-auto"
           data-testid="audit-event-list">
        {loading && events.length === 0 && (
          <div className="p-6 text-center text-xs text-zinc-500">
            <ArrowsClockwise className="animate-spin mx-auto mb-2" size={18} />
            読み込み中…
          </div>
        )}
        {!loading && events.length === 0 && (
          <div className="p-6 text-center text-xs text-zinc-400">
            該当するイベントがありません。
          </div>
        )}
        {events.map((e, idx) => {
          const meta = EVENT_META[e.event] || FALLBACK_META;
          const Icon = meta.icon;
          const when = e.ts ? formatDateTime(e.ts) : '';
          return (
            <div
              key={`${e.approval_id}-${e.ts}-${idx}`}
              className="flex items-start gap-2 p-2.5 hover:bg-indigo-50/30 transition-colors"
              data-testid={`audit-event-${idx}`}
            >
              <span className={`shrink-0 inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold ${meta.tone}`}>
                <Icon size={10} weight="fill" />
                {meta.label}
              </span>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-zinc-800 flex items-center gap-1.5 flex-wrap">
                  <span className="font-mono font-semibold" data-testid={`audit-event-${idx}-order`}>
                    {e.order_no || '(発注不明)'}
                  </span>
                  {e.from_email && (
                    <span className="text-zinc-500 truncate max-w-[160px]">{e.from_email}</span>
                  )}
                  {e.to_email && (
                    <>
                      <ArrowRight size={10} className="text-zinc-400 shrink-0" />
                      <span className="text-zinc-700 font-medium truncate max-w-[160px]">{e.to_email}</span>
                    </>
                  )}
                  {e.event === 'escalated' && (e.escalation_count > 0) && (
                    <span className="ml-1 inline-flex items-center rounded-full bg-amber-100 text-amber-800 px-1.5 py-0 text-[9px]">
                      #{e.escalation_count}
                    </span>
                  )}
                </div>
                {e.note && (
                  <div className="mt-0.5 text-[11px] text-zinc-500 italic truncate">
                    {e.note}
                  </div>
                )}
                <div className="mt-0.5 text-[10px] text-zinc-400 flex items-center gap-2">
                  <span>{when}</span>
                  {e.source && (
                    <>
                      <span>·</span>
                      <span className="text-indigo-500 font-mono">{e.source}</span>
                    </>
                  )}
                  <span>·</span>
                  <span>現在状態: {e.current_status}</span>
                </div>
              </div>
              {e.current_status === 'pending' && (
                <button
                  type="button"
                  onClick={() => remind(e.approval_id, e.order_no)}
                  disabled={remindingId === e.approval_id}
                  className="shrink-0 inline-flex items-center gap-1 rounded border border-amber-200 bg-amber-50 hover:bg-amber-100 disabled:opacity-50 px-2 py-1 text-[10px] font-semibold text-amber-800 transition-colors"
                  title={`承認者 ${e.current_approver_email} へリマインド送信（4h に1回まで）`}
                  data-testid={`audit-event-${idx}-remind`}
                >
                  {remindingId === e.approval_id ? (
                    <ArrowsClockwise className="animate-spin" size={10} />
                  ) : (
                    <BellRinging size={10} weight="fill" />
                  )}
                  リマインド
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StatChip({ label, value, tone, testid }) {
  return (
    <div className={`rounded-lg border px-3 py-2 flex flex-col ${tone}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-lg font-bold leading-tight">{value}</div>
    </div>
  );
}
